'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useT } from '@/lib/useT';
import { Shield, Cookie } from 'lucide-react';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

const STORAGE_KEY = 'logixa_cookie_consent';

export default function CookieConsent() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Small delay for slide-up animation
        const timer = setTimeout(() => setVisible(true), 1000);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage not available
    }
  }, []);

  // Re-open the banner on demand (e.g. footer "Cookie-Einstellungen" link
  // dispatches this event), so visitors can change their choice anytime.
  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener('logixa-open-cookies', open);
    return () => window.removeEventListener('logixa-open-cookies', open);
  }, []);

  const saveConsent = (settings: CookieSettings) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // localStorage not available
    }
    setVisible(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    });
  };

  const saveSelection = () => {
    saveConsent({
      necessary: true,
      analytics,
      marketing,
      timestamp: Date.now(),
    });
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
      style={{ animation: 'cookie-slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
    >
      <style>{`
        @keyframes cookie-slide-up {
          from { opacity: 0; transform: translateY(100%); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        className="max-w-4xl mx-auto p-6 md:p-8"
        style={{
          background: 'var(--c-bg)',
          border: '1px solid var(--c-border2)',
          borderRadius: '10px',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: '6px' }}>
            <Shield size={20} />
          </div>
          <div>
            <h3 className="text-base font-display font-bold" style={{ color: 'var(--c-heading)' }}>{t('cookie_title')}</h3>
            <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{t('cookie_subtitle')}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm mb-6 leading-relaxed" style={{ color: 'var(--c-text)' }}>
          Diese Website verwendet Cookies, um Ihnen die bestmoegliche Nutzung zu ermoeglichen.
          Weitere Informationen finden Sie in unserer{' '}
          <Link href="https://logixa.one/datenschutz" className="underline" style={{ color: 'var(--link)' }}>
            Datenschutzerklaerung
          </Link>.
        </p>

        {/* Toggles */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Necessary - always on */}
          <div className="flex items-center gap-3 px-4 py-3"
            style={{ background: 'color-mix(in srgb, var(--live) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--live) 25%, transparent)', borderRadius: '6px' }}>
            <div className="relative">
              <div className="w-10 h-5 rounded-full" style={{ background: 'color-mix(in srgb, var(--live) 40%, transparent)' }}>
                <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full" style={{ background: 'var(--live)' }} />
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold block" style={{ color: 'var(--c-heading)' }}>{t('cookie_necessary')}</span>
              <span className="text-[10px]" style={{ color: 'var(--c-muted)' }}>{t('cookie_always_on')}</span>
            </div>
          </div>

          {/* Analytics */}
          <button
            type="button"
            onClick={() => setAnalytics(!analytics)}
            className="flex items-center gap-3 px-4 py-3 transition-all cursor-pointer text-left"
            style={{
              background: analytics ? 'color-mix(in srgb, var(--live) 6%, transparent)' : 'var(--c-panel)',
              border: `1px solid ${analytics ? 'color-mix(in srgb, var(--live) 22%, transparent)' : 'var(--c-border2)'}`,
              borderRadius: '6px',
            }}
          >
            <div className="relative w-10 h-5 flex-shrink-0">
              <div className="w-10 h-5 rounded-full transition-colors"
                style={{ background: analytics ? 'color-mix(in srgb, var(--live) 40%, transparent)' : 'var(--c-border2)' }}>
                <div
                  className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                  style={{
                    background: analytics ? 'var(--live)' : 'var(--c-muted)',
                    left: analytics ? 'calc(100% - 18px)' : '2px',
                  }}
                />
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold block" style={{ color: 'var(--c-heading)' }}>{t('cookie_analytics')}</span>
              <span className="text-[10px]" style={{ color: 'var(--c-muted)' }}>{analytics ? 'Aktiv' : 'Inaktiv'}</span>
            </div>
          </button>

          {/* Marketing */}
          <button
            type="button"
            onClick={() => setMarketing(!marketing)}
            className="flex items-center gap-3 px-4 py-3 transition-all cursor-pointer text-left"
            style={{
              background: marketing ? 'color-mix(in srgb, var(--live) 6%, transparent)' : 'var(--c-panel)',
              border: `1px solid ${marketing ? 'color-mix(in srgb, var(--live) 22%, transparent)' : 'var(--c-border2)'}`,
              borderRadius: '6px',
            }}
          >
            <div className="relative w-10 h-5 flex-shrink-0">
              <div className="w-10 h-5 rounded-full transition-colors"
                style={{ background: marketing ? 'color-mix(in srgb, var(--live) 40%, transparent)' : 'var(--c-border2)' }}>
                <div
                  className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                  style={{
                    background: marketing ? 'var(--live)' : 'var(--c-muted)',
                    left: marketing ? 'calc(100% - 18px)' : '2px',
                  }}
                />
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold block" style={{ color: 'var(--c-heading)' }}>{t('cookie_marketing')}</span>
              <span className="text-[10px]" style={{ color: 'var(--c-muted)' }}>{marketing ? 'Aktiv' : 'Inaktiv'}</span>
            </div>
          </button>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={acceptAll} className="btn-primary flex-1 text-sm text-center">
            Alle akzeptieren
          </button>
          <button onClick={saveSelection} className="btn-ghost flex-1 text-sm text-center">
            Auswahl speichern
          </button>
        </div>
      </div>
    </div>
  );
}
